import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, tokens } from '../api/client';

/** Etapas do produto que a instalação pode ter construído e ainda não liberado. */
export interface Recursos {
  jornadas: boolean;
}

export interface UsuarioLogado {
  id: string;
  name: string;
  email: string;
  scope: 'SUPERADMIN' | 'ORGANIZATION';
  organizationId: string | null;
  permissions: string[];
  organization?: { id: string; tradeName: string; status: string } | null;
  /**
   * Vem do `/auth/me`. Opcional porque a resposta do login não a traz — e nesse
   * intervalo o padrão é o mesmo do servidor: trancado.
   */
  recursos?: Recursos;
}

interface RespostaLogin {
  accessToken: string;
  refreshToken: string;
  user: UsuarioLogado;
}

interface AuthContextValue {
  usuario: UsuarioLogado | null;
  carregando: boolean;
  /** `lembrar` mantém a sessão depois de fechar o navegador. Ver `tokens.salvar`. */
  entrar(email: string, senha: string, lembrar?: boolean): Promise<void>;
  sair(): Promise<void>;
  /**
   * Relê o `/auth/me`.
   *
   * Este contexto não passa pelo TanStack Query — guarda o usuário em `useState`
   * —, então invalidar chave nenhuma o atualiza. Sem isto, trocar o nome
   * fantasia em Configurações mudava a tela e deixava o nome antigo na barra
   * lateral até alguém apertar F5, e a pessoa saía achando que não tinha salvo.
   */
  recarregar(): Promise<void>;
  /** `true` se o usuário tem TODAS as permissões pedidas. Superadmin sempre passa. */
  pode(...permissoes: string[]): boolean;
  /**
   * `true` se a INSTALAÇÃO já liberou a etapa. Diferente de `pode`: aqui não
   * há papel que ajude — nem o Superadmin passa, porque a etapa está desligada
   * para todo mundo até a chave virar no servidor.
   */
  liberado(recurso: keyof Recursos): boolean;
  ehSuperadmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioLogado | null>(null);
  const [carregando, setCarregando] = useState(true);

  // Reidrata a sessão ao abrir o app. O token pode estar vencido: o cliente
  // renova sozinho, e só se a renovação falhar é que a sessão é descartada.
  useEffect(() => {
    let ativo = true;

    (async () => {
      if (!tokens.access()) {
        setCarregando(false);
        return;
      }

      try {
        const eu = await api<UsuarioLogado>('/auth/me');
        if (ativo) setUsuario(eu);
      } catch {
        tokens.limpar();
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string, lembrar = false) => {
    const resposta = await api<RespostaLogin>('/auth/login', {
      method: 'POST',
      body: { email, password: senha },
      semRenovacao: true,
    });

    tokens.salvar(resposta.accessToken, resposta.refreshToken, lembrar);

    // O login devolve o essencial; o /me traz a organização junto, que a
    // topbar mostra. Uma chamada a mais, uma tela sem "piscar" depois.
    const eu = await api<UsuarioLogado>('/auth/me').catch(() => resposta.user);
    setUsuario(eu);
  }, []);

  // Falha em silêncio: quem chama está confirmando um salvamento que já deu
  // certo, e transformar isso num erro de tela culparia a operação errada.
  const recarregar = useCallback(async () => {
    const eu = await api<UsuarioLogado>('/auth/me').catch(() => null);
    if (eu) setUsuario(eu);
  }, []);

  const sair = useCallback(async () => {
    // Encerra a sessão no servidor para o refresh token morrer de verdade;
    // se a chamada falhar, limpar o lado do cliente ainda é o certo.
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    tokens.limpar();
    setUsuario(null);
  }, []);

  const valor = useMemo<AuthContextValue>(() => {
    const permissoes = new Set(usuario?.permissions ?? []);

    return {
      usuario,
      carregando,
      entrar,
      sair,
      recarregar,
      ehSuperadmin: usuario?.scope === 'SUPERADMIN',
      pode: (...pedidas) => pedidas.every((p) => permissoes.has(p)),
      // Ausente = trancado. Enquanto o /me não voltou, o certo é não oferecer
      // um caminho que pode terminar em 403.
      liberado: (recurso) => usuario?.recursos?.[recurso] === true,
    };
  }, [usuario, carregando, entrar, sair, recarregar]);

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return ctx;
}
